import React, { useEffect, useMemo, useState } from "react";
import { Grid, Typography } from "@mui/material";
import EntityHubLayout, { PAGE_SIZE } from "../../components/product-manager/EntityHubLayout";
import EntityAssetCard from "../../components/product-manager/EntityAssetCard";
import EditKafkaEventTopicDialog from "../../components/product-manager/EditKafkaEventTopicDialog";
import {
  filterByHubState,
  useBookmarkableHubState,
} from "../../hooks/useBookmarkableHubState";
import { useKafkaEventTopics } from "../../hooks/useKafkaEventTopics";
import { apiService } from "../../api/services/apiService";
import type { KafkaEventTopic } from "../../types";
import { EntityStatus } from "../../types";
import { pmHubAddPath } from "../../utils/pmHubRoutes";

const KafkaEventHubPage: React.FC = () => {
  const hubState = useBookmarkableHubState();
  const { topics: fetched, loading, error } = useKafkaEventTopics();
  const [topics, setTopics] = useState<KafkaEventTopic[]>([]);
  const [toggleLoadingId, setToggleLoadingId] = useState<string | null>(null);

  useEffect(() => {
    setTopics(fetched);
  }, [fetched]);

  const filtered = useMemo(
    () =>
      filterByHubState(topics, hubState, undefined, (t) => [
        t.name,
        t.environment,
        t.purpose ?? "",
        t.description ?? "",
        t.schemaSubject ?? "",
        t.schemaFormat,
      ]),
    [topics, hubState],
  );

  const paginated = useMemo(() => {
    const start = (hubState.page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, hubState.page]);

  const editItem = useMemo(
    () => topics.find((t) => t.uuid === hubState.editUuid) ?? null,
    [topics, hubState.editUuid],
  );

  const handleToggle = async (item: KafkaEventTopic) => {
    setToggleLoadingId(item.uuid);
    const newStatus =
      item.status === EntityStatus.ACTIVE
        ? EntityStatus.INACTIVE
        : EntityStatus.ACTIVE;
    try {
      const updated = await apiService.updateKafkaEventTopic(item.uuid, {
        status: newStatus,
      });
      setTopics((prev) => prev.map((t) => (t.uuid === updated.uuid ? updated : t)));
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : "Failed to update status");
    } finally {
      setToggleLoadingId(null);
    }
  };

  return (
    <>
      <EntityHubLayout
        hubTitle="Kafka Event Registry"
        hubDescription="Register Kafka topics, schemas, and metadata for your event catalog."
        addPath={pmHubAddPath("kafkaEvent")}
        addLabel="Add topic"
        loading={loading}
        error={error}
        itemCount={filtered.length}
        hubState={hubState}
        filterSidebarExtra={
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
            Topics are unique per environment. All authenticated users can browse via Event Explorer.
          </Typography>
        }
      >
        <Grid container spacing={2}>
          {paginated.map((topic) => (
            <Grid item xs={12} sm={6} md={4} key={topic.uuid}>
              <EntityAssetCard
                title={topic.name}
                subtitle={`${topic.environment} · ${topic.schemaFormat}${topic.schemaVersion ? ` · v${topic.schemaVersion}` : ""}`}
                status={topic.status}
                enabled={topic.status === EntityStatus.ACTIVE}
                toggleLoading={toggleLoadingId === topic.uuid}
                onToggleEnabled={() => handleToggle(topic)}
                onEdit={() => hubState.setEditUuid(topic.uuid)}
                onDelete={async () => {
                  await apiService.deleteKafkaEventTopic(topic.uuid);
                  setTopics((prev) => prev.filter((t) => t.uuid !== topic.uuid));
                }}
              />
            </Grid>
          ))}
          {!loading && filtered.length === 0 && (
            <Grid item xs={12}>
              <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                No Kafka event topics match your filters.
              </Typography>
            </Grid>
          )}
        </Grid>
      </EntityHubLayout>

      <EditKafkaEventTopicDialog
        item={editItem}
        open={!!editItem}
        onClose={() => hubState.setEditUuid(null)}
        onSaved={(updated) => {
          setTopics((prev) => prev.map((t) => (t.uuid === updated.uuid ? updated : t)));
          hubState.setEditUuid(null);
        }}
      />
    </>
  );
};

export default KafkaEventHubPage;
